import { useCallback, useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router'

import { OlarkHelper } from '@fv/client-components'

import { supportMessage } from '@/constants'
import { useClearCustomerRoutedDraft } from '@/features/customer-routed/hooks'
import {
  ShipmentDetailsSliderPanel,
  type ShipmentSliderState,
} from '@/features/shipment-details-panel/ShipmentDetailsSliderPanel'
import type { SetActiveSlider } from '@/features/shipment-details-panel/types'
import { useEditBookingPage, useLoad } from '@/hooks/shipments'
import { routes } from '@/routes'

import InfoBox from '../InfoBox'
import { ActionsPanel } from './ActionsPanel'
import { DetailsPanel } from './DetailsPanel'
import { Footer } from './Footer'
import { OverviewPanel } from './OverviewPanel'

export const ShipmentDetailsPage = () => {
  const [sliderState, setSliderState] = useState<ShipmentSliderState>()
  const { loadId } = useParams<{ loadId: string }>()
  const loadQuery = useLoad(loadId, { refetchWhileDispatching: true })
  const load = loadQuery.data
  const { status } = load ?? {}
  const { composeEditLink } = useEditBookingPage(load)
  const clearCustomerRoutedDraft = useClearCustomerRoutedDraft()

  const setActiveSlider = useCallback<SetActiveSlider['setActiveSlider']>(
    (tab, tabProps) =>
      tab ? setSliderState({ tab, tabProps }) : setSliderState(undefined),
    [],
  )

  useEffect(() => {
    clearCustomerRoutedDraft()
  }, [clearCustomerRoutedDraft])

  if (loadQuery.isLoading || !loadQuery.data || !loadId) {
    return (
      <div className="shipper-page-fill flex flex-col">
        <div className="flex flex-1 items-center justify-center">
          {loadQuery.isLoading ? (
            <InfoBox icon="sync">Loading shipment...</InfoBox>
          ) : (
            <InfoBox icon="exclamation-triangle">
              Unable to load shipment, {supportMessage}
            </InfoBox>
          )}
        </div>
      </div>
    )
  }

  if (load.quoteProcessStatus === 'rerate-ready') {
    return (
      <Navigate to={routes.book({ loadId, quoteId: load.selectedQuote._id })} />
    )
  }

  if (status === 'pending') {
    return <Navigate to={`/search?text=${loadId}`} replace />
  }

  if (
    load?.selectedQuote &&
    (load?.pickup?.status === 'error' ||
      (load?.pickup?.status === 'not-requested' &&
        load?.bol?.status === 'error'))
  ) {
    return <Navigate to={composeEditLink()} />
  }

  return (
    <>
      <div className="shipper-page-fill grid min-h-0 grid-rows-[minmax(0,1fr)_auto] bg-fv-gray-100 grid-cols-[22rem_18rem_1fr] b1350:grid-cols-[18rem_1fr] b1350:grid-rows-[auto_minmax(0,1fr)_auto]">
        <OverviewPanel load={loadQuery.data} />
        <ActionsPanel load={loadQuery.data} setActiveSlider={setActiveSlider} />
        <DetailsPanel load={loadQuery.data} setActiveSlider={setActiveSlider} />
        <Footer load={loadQuery.data} />
      </div>

      <ShipmentDetailsSliderPanel
        loadId={loadId}
        state={sliderState}
        onChange={setSliderState}
      />
      <OlarkHelper loadId={loadQuery.data?.loadId} />
    </>
  )
}
